import { useEffect, useState } from 'react';
import {
  makeStyles,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import { useGenaiBuilderApi, Agent } from './useGenaiBuilderApi';
import { useIsAdmin } from './useIsAdmin';
import { AgentForm } from './AgentForm';
import { AgentDocuments } from './AgentDocuments';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  slug: {
    fontFamily: 'monospace',
    fontSize: '0.85em',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  description: {
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  error: {
    color: theme.palette.error.main,
    padding: theme.spacing(2),
  },
}));

export function AgentsList() {
  const classes = useStyles();
  const api = useGenaiBuilderApi();
  const { isAdmin } = useIsAdmin();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [deletingAgent, setDeletingAgent] = useState<Agent | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [viewingDocumentsFor, setViewingDocumentsFor] = useState<Agent | null>(null);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      setAgents(await api.listAgents());
    } catch (err: any) {
      setError(err.message ?? 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const handleSaved = (_agent: Agent) => {
    setShowForm(false);
    setEditingAgent(null);
    fetchAgents();
  };

  const handleDeleteConfirm = async () => {
    if (!deletingAgent) return;
    setDeleting(true);
    try {
      await api.deleteAgent(deletingAgent.id);
      setDeletingAgent(null);
      fetchAgents();
    } catch (err: any) {
      setError(err.message ?? 'Failed to delete agent');
    } finally {
      setDeleting(false);
    }
  };

  if (showForm || editingAgent) {
    return (
      <Box className={classes.root}>
        <Typography variant="h6" gutterBottom>
          {editingAgent ? `Edit Agent: ${editingAgent.name}` : 'New Agent'}
        </Typography>
        <AgentForm
          agent={editingAgent ?? undefined}
          onSaved={handleSaved}
          onCancel={() => {
            setShowForm(false);
            setEditingAgent(null);
          }}
        />
      </Box>
    );
  }

  if (viewingDocumentsFor) {
    return (
      <AgentDocuments
        agent={viewingDocumentsFor}
        onBack={() => setViewingDocumentsFor(null)}
      />
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h6">Agents</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowForm(true)}
          >
            New Agent
          </Button>
        )}
      </Box>

      {loading && <CircularProgress size={32} />}

      {error && (
        <Typography className={classes.error}>{error}</Typography>
      )}

      {!loading && agents.length === 0 && (
        <Box className={classes.empty}>
          <Typography variant="body1">
            No agents yet. Create your first agent to get started.
          </Typography>
        </Box>
      )}

      {!loading && agents.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ whiteSpace: 'nowrap' }}>Name</TableCell>
              <TableCell style={{ whiteSpace: 'nowrap' }}>Slug</TableCell>
              <TableCell style={{ width: '100%' }}>Description</TableCell>
              <TableCell style={{ whiteSpace: 'nowrap' }}>Created</TableCell>
              <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agents.map((agent) => (
              <TableRow key={agent.id} hover>
                <TableCell style={{ whiteSpace: 'nowrap' }}>{agent.name}</TableCell>
                <TableCell>
                  <span className={classes.slug}>{agent.slug}</span>
                </TableCell>
                <TableCell>
                  <span className={classes.description}>
                    {agent.description || (
                      <em style={{ opacity: 0.5 }}>No description</em>
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  {new Date(agent.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Manage documents">
                    <IconButton
                      size="small"
                      onClick={() => setViewingDocumentsFor(agent)}
                    >
                      <FolderOpenIcon />
                    </IconButton>
                  </Tooltip>
                  {isAdmin && (
                    <Tooltip title="Edit agent">
                      <IconButton
                        size="small"
                        onClick={() => setEditingAgent(agent)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isAdmin && (
                    <Tooltip title="Delete agent">
                      <IconButton
                        size="small"
                        onClick={() => setDeletingAgent(agent)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(deletingAgent)}
        onClose={() => setDeletingAgent(null)}
      >
        <DialogTitle>Delete Agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deletingAgent?.name}"? This action
            will soft-delete the agent and can be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeletingAgent(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="secondary"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
